import { router } from '@inertiajs/react'
import { useMemo, useState } from 'react'

import { titleize } from '../../../utils/string'

function ErrorText({ errors, name }) {
  const msg = errors?.[name]
  if (!msg) return null
  const text = Array.isArray(msg) ? msg.join(', ') : String(msg)
  return <div className="invalid-feedback d-block">{text}</div>
}

function multiSelectValues(e) {
  return Array.from(e.target.selectedOptions).map((o) => o.value)
}

export default function Edit({ employee, roles_options, errors }) {
  const rolesOptions = roles_options || ['user', 'manager', 'hr_manager', 'super_admin']
  const employmentStatusOptions = ['contractual', 'probationary', 'permanent', 'professional']

  const initialRoles = useMemo(() => {
    const r = employee?.roles || []
    return r.length ? r : ['user']
  }, [employee])

  const [form, setForm] = useState({
    roles: initialRoles,
    phone: employee?.phone || '',
    birthday: employee?.birthday || '',
    employment_status: employee?.employment_status || '',
    contract_start_date: employee?.contract_start_date || '',
    contract_end_date: employee?.contract_end_date || '',
    gross_salary: employee?.gross_salary ?? '',
    hourly_rate: employee?.hourly_rate ?? '',
    is_daily_report_required: employee?.is_daily_report_required ?? true,
  })

  const showGrossSalary = form.employment_status === 'permanent'
  const showContractDates = ['contractual', 'professional'].includes(form.employment_status)
  const showHourlyRate = form.employment_status === 'contractual'

  const submit = (e) => {
    e.preventDefault()
    router.patch(`/company_settings/employees/${employee.id}`, {
      user: {
        roles: form.roles,
        phone: form.phone,
        birthday: form.birthday,
        employment_status: form.employment_status || null,
        contract_start_date: form.contract_start_date || null,
        contract_end_date: form.contract_end_date || null,
        gross_salary: form.gross_salary === '' ? null : form.gross_salary,
        hourly_rate: form.hourly_rate === '' ? null : form.hourly_rate,
        is_daily_report_required: form.is_daily_report_required,
      },
    })
  }

  return (
    <div className="container-fluid">
      <div className="header">
        <div className="header-body">
          <div className="row align-items-center">
            <div className="col">
              <h6 className="header-pretitle">Human Resources</h6>
              <h1 className="header-title text-truncate">Edit Employee</h1>
            </div>
            <div className="col-auto">
              <button type="button" className="btn btn-white" onClick={() => router.visit('/company_settings/employees')}>
                Back to users list
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="row justify-content-center">
        <div className="col-12 col-lg-10 col-xl-8">
          <form onSubmit={submit}>
            <div className="card">
              <div className="card-body">
                <div className="row justify-content-between align-items-center">
                  <div className="col">
                    <div className="row align-items-center">
                      <div className="col-auto">
                        <div className="avatar">
                          <img
                            src={employee.avatar_url}
                            className="avatar-img rounded-circle"
                            alt={employee.full_name || employee.email}
                          />
                        </div>
                      </div>
                      <div className="col ms-n2">
                        <h4 className="mb-1">{employee?.full_name || employee?.email || ''}</h4>
                        <small className="text-body-secondary">{employee?.email || ''}</small>
                      </div>
                    </div>
                  </div>
                </div>

                <hr className="my-5" />

                <div className="row">
                  <div className="col-12">
                    <div className="form-group">
                      <label className="form-label">Avatar URL</label>
                      <input
                        className="form-control-plaintext"
                        value={employee?.avatar_url || ''}
                        readOnly
                        disabled
                      />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Roles</label>
                      <select
                        multiple
                        className="form-select"
                        value={form.roles}
                        onChange={(e) => setForm((f) => ({ ...f, roles: multiSelectValues(e) }))}
                      >
                        {rolesOptions.map((r) => (
                          <option key={r} value={r}>
                            {titleize(r)}
                          </option>
                        ))}
                      </select>
                      <ErrorText errors={errors} name="roles" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Phone</label>
                      <input
                        className="form-control"
                        value={form.phone}
                        onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
                      />
                      <ErrorText errors={errors} name="phone" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Birthday</label>
                      <input
                        type="date"
                        className="form-control"
                        value={form.birthday}
                        onChange={(e) => setForm((f) => ({ ...f, birthday: e.target.value }))}
                      />
                      <ErrorText errors={errors} name="birthday" />
                    </div>
                  </div>

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Employment Status</label>
                      <select
                        className="form-select"
                        value={form.employment_status}
                        onChange={(e) => setForm((f) => ({ ...f, employment_status: e.target.value }))}
                      >
                        <option value="">(not set)</option>
                        {employmentStatusOptions.map((s) => (
                          <option key={s} value={s}>
                            {titleize(s)}
                          </option>
                        ))}
                      </select>
                      <ErrorText errors={errors} name="employment_status" />
                    </div>
                  </div>

                  {showContractDates ? (
                    <>
                      <div className="col-12 col-md-6">
                        <div className="form-group">
                          <label className="form-label">Contract Start Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={form.contract_start_date}
                            onChange={(e) => setForm((f) => ({ ...f, contract_start_date: e.target.value }))}
                          />
                          <ErrorText errors={errors} name="contract_start_date" />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <div className="form-group">
                          <label className="form-label">Contract End Date</label>
                          <input
                            type="date"
                            className="form-control"
                            value={form.contract_end_date}
                            onChange={(e) => setForm((f) => ({ ...f, contract_end_date: e.target.value }))}
                          />
                          <ErrorText errors={errors} name="contract_end_date" />
                        </div>
                      </div>
                    </>
                  ) : null}

                  {showHourlyRate ? (
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label className="form-label">Hourly Rate</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={form.hourly_rate}
                          onChange={(e) => setForm((f) => ({ ...f, hourly_rate: e.target.value }))}
                        />
                        <ErrorText errors={errors} name="hourly_rate" />
                      </div>
                    </div>
                  ) : null}

                  {showGrossSalary ? (
                    <div className="col-12 col-md-6">
                      <div className="form-group">
                        <label className="form-label">Gross Salary</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          value={form.gross_salary}
                          onChange={(e) => setForm((f) => ({ ...f, gross_salary: e.target.value }))}
                        />
                        <small className="text-body-secondary">As indicated in the employment contract or last increase</small>
                        <ErrorText errors={errors} name="gross_salary" />
                      </div>
                    </div>
                  ) : null}

                  <div className="col-12 col-md-6">
                    <div className="form-group">
                      <label className="form-label">Telegram Chat ID</label>
                      <input className="form-control-plaintext" value={employee.tg_chat_id || ''} readOnly disabled />
                    </div>
                  </div>

                  <div className="col-12">
                    <div className="form-group">
                      <div className="form-check">
                        <input
                          id="is_daily_report_required"
                          type="checkbox"
                          className="form-check-input"
                          checked={form.is_daily_report_required}
                          onChange={(e) => setForm((f) => ({ ...f, is_daily_report_required: e.target.checked }))}
                        />
                        <label className="form-check-label" htmlFor="is_daily_report_required">
                          Daily Report Required
                        </label>
                      </div>
                      <ErrorText errors={errors} name="is_daily_report_required" />
                    </div>
                  </div>

                  <div className="col-12">
                    <button type="submit" className="btn btn-primary">
                      Save Changes
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </div>
  )
}
